'use client';

import Link from 'next/link';
import { useEffect, useState, type CSSProperties } from 'react';
import { GRID_ENABLED } from '../connections/lib/daily-game';
import {
  ALL_GAMES,
  GAME_META,
  fetchProgress,
  type DailyGame,
} from './dailyGames';

/** "7/14" — the tightest date form; the row has to fit an iPhone next to
 *  the game name, and weekday/month words were the width that didn't. */
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}`;
}

// Cross-game "keep playing" footer shown on every end screen. For each of the
// OTHER daily games it resolves the player's *next unplayed* puzzle (on or
// before the date being viewed) via the game worker's /progress endpoint, and
// links straight to it — turning a one-game player into a multi-game player.
//
// Identity mirrors the rest of the game client: the signed-in X-User-Id (spans
// devices) plus the per-device per-game session key from localStorage, so the
// link is personalised for logged-out players too. Falls back to the most
// recent puzzle (a replay) when caught up, and renders nothing for a game it
// can't resolve (e.g. before the worker ships the endpoint) — never a dead link.

interface Resolved {
  game: DailyGame;
  date: string;
  fresh: boolean; // true = your next unplayed; false = replay the latest
}

export function KeepPlayingFooter({
  game,
  date,
  userId,
  archiveHref,
  includeSelf,
}: {
  /** The game whose end screen this is — excluded from the cross-links
   *  unless `includeSelf` opts it back in. */
  game: DailyGame;
  /** The date being viewed; bounds "next unplayed" to on-or-before this date. */
  date: string;
  userId?: string;
  /** When set, renders a same-game "Archive" link. All four games pass this;
   *  day-stepping lives in the shared DailySlateNav (whose center links to
   *  /games), so the archive link belongs here. */
  archiveHref?: string;
  /** Also link this game's own back-catalog: the most recent unplayed puzzle
   *  strictly BEFORE `date` (never a replay, and never today's — its result
   *  POST may still be in flight when this mounts). Renders first. */
  includeSelf?: boolean;
}) {
  const [links, setLinks] = useState<Resolved[]>([]);

  useEffect(() => {
    const targets = [
      ...(includeSelf ? [game] : []),
      ...ALL_GAMES.filter(
        (g) => g !== game && (g !== 'grid' || GRID_ENABLED),
      ),
    ];
    let cancelled = false;

    Promise.all(
      targets.map(async (g): Promise<Resolved | null> => {
        const p = await fetchProgress(g, { userId, onOrBefore: date });
        if (!p) return null;
        if (g === game) {
          const self = p.entries.find((e) => !e.played && e.date < date);
          return self ? { game: g, date: self.date, fresh: true } : null;
        }
        const target = p.nextUnplayed ?? p.latest;
        if (!target) return null;
        return { game: g, date: target, fresh: p.nextUnplayed != null };
      }),
    ).then((rows) => {
      if (!cancelled) {
        setLinks(rows.filter((r): r is Resolved => r !== null));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [game, date, userId, includeSelf]);

  if (links.length === 0 && !archiveHref) return null;

  // flex-wrap + the status span's margin-left:auto keep rows one line where
  // they fit and drop the status to its own right-aligned line where they
  // don't. The status text itself stays nowrap so it never breaks mid-phrase
  // — without the wrap escape hatch it used to force the whole page wider
  // than an iPhone viewport (horizontal scroll).
  const rowStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '2px 12px',
    padding: '11px 14px',
    borderRadius: 'var(--radius-md, 10px)',
    border: '1px solid var(--border-soft)',
    background: 'var(--card-bg)',
    textDecoration: 'none',
  };

  return (
    <div
      className="mo-bg-card"
      style={{
        marginTop: 16,
        padding: 16,
        border: '1px solid var(--border-soft)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--card-stack)',
        textAlign: 'left',
      }}
    >
      <p
        style={{
          margin: '0 0 10px',
          fontFamily: 'var(--font-sans)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--fg-2)',
        }}
      >
        Keep playing
      </p>

      <div style={{ display: 'grid', gap: 8 }}>
        {links.map((l) => (
          <Link
            key={l.game}
            href={GAME_META[l.game].href(l.date)}
            style={rowStyle}
          >
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 15,
                color: 'var(--fg-1)',
              }}
            >
              {GAME_META[l.game].label}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12.5,
                color: 'var(--acc-deep)',
                whiteSpace: 'nowrap',
                marginLeft: 'auto',
              }}
            >
              {l.fresh ? 'Unplayed' : 'Replay'} · {shortDate(l.date)} →
            </span>
          </Link>
        ))}
      </div>

      {archiveHref && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Link
            href={archiveHref}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--acc-deep)',
              textDecoration: 'none',
            }}
          >
            Archive →
          </Link>
        </div>
      )}
    </div>
  );
}
